// ============================================================================
// CONFIG SETTING FIELD
// ============================================================================
// Renders a single config definition as the appropriate input control,
// driven by definition.dataType. Shows Override / Inherited / System Only
// status badges.

import React from 'react';
import { SyutSelect } from 'components/ui';
import type { ConfigDefinition, ConfigEffectiveValue, ConfigListItem } from 'api/types/config.types';
import ConfigListEditor from './ConfigListEditor';

export type ScopeTab = 'SYSTEM' | 'ENTITY' | 'PRODUCT';

interface ConfigSettingFieldProps {
  definition: ConfigDefinition;
  effective: ConfigEffectiveValue | undefined;
  scopeType: ScopeTab;
  scopeLevel: number;
  /** Pending edit value (string for scalar, undefined if unedited). */
  editedValue?: string | null;
  /** Pending edit list values for is_multi_value definitions. */
  editedListValues?: ConfigListItem[];
  /** True when user has marked the override for removal at this scope. */
  isCleared: boolean;
  onScalarChange: (value: string | null) => void;
  onListChange: (items: ConfigListItem[]) => void;
  onClearOverride: () => void;
  disabled?: boolean;
}

const Badge: React.FC<{ tone: 'override' | 'inherited' | 'system-only'; children: React.ReactNode }> = ({ tone, children }) => {
  const styles: Record<string, { bg: string; fg: string; border: string }> = {
    'override':    { bg: 'rgba(37,99,235,0.10)',  fg: '#2563eb', border: '#2563eb' },
    'inherited':   { bg: 'rgba(107,114,128,0.10)', fg: '#6b7280', border: '#9ca3af' },
    'system-only': { bg: 'rgba(217,119,6,0.10)',   fg: '#d97706', border: '#d97706' },
  };
  const s = styles[tone];
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '0.65rem',
      fontWeight: 600,
      padding: '1px 6px',
      borderRadius: '999px',
      color: s.fg,
      background: s.bg,
      border: `1px solid ${s.border}`,
      letterSpacing: '0.02em',
    }}>{children}</span>
  );
};

const ConfigSettingField: React.FC<ConfigSettingFieldProps> = ({
  definition, effective, scopeType, scopeLevel,
  editedValue, editedListValues, isCleared,
  onScalarChange, onListChange, onClearOverride,
  disabled,
}) => {
  const allowsThisScope = scopeLevel <= definition.maxScopeLevel;
  const editingDisabled = disabled || !allowsThisScope;

  // Resolve the value the field should display
  const currentScalar = editedValue !== undefined && editedValue !== null
    ? editedValue
    : isCleared
      ? (effective?.defaultValue ?? '')
      : (effective?.effectiveValue ?? '');

  const currentList: ConfigListItem[] = editedListValues
    ?? (isCleared ? [] : (effective?.listValues ?? []));

  // Resolution badge
  const isOverride = scopeType !== 'SYSTEM'
    && effective?.resolvedFromScopeType === scopeType
    && !isCleared
    && editedValue === undefined && editedListValues === undefined;
  const isInherited = scopeType !== 'SYSTEM'
    && (!effective?.resolvedFromScopeType
      || effective?.resolvedFromScopeType !== scopeType
      || isCleared);
  const isSystemOnly = !allowsThisScope;

  const renderControl = () => {
    if (definition.isMultiValue) {
      return (
        <ConfigListEditor
          values={currentList}
          onChange={onListChange}
          disabled={editingDisabled}
        />
      );
    }

    switch (definition.dataType) {
      case 'Boolean':
        return (
          <SyutSelect
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={currentScalar || 'false'}
            onChange={(v) => onScalarChange((v as string) || 'false')}
            isClearable={false}
            isDisabled={editingDisabled}
            size="sm"
          />
        );
      case 'Integer':
      case 'Decimal':
        return (
          <input
            type="number"
            className="form-input"
            value={currentScalar}
            min={definition.minValue ?? undefined}
            max={definition.maxValue ?? undefined}
            step={definition.dataType === 'Integer' ? 1 : 'any'}
            disabled={editingDisabled}
            onChange={(e) => onScalarChange(e.target.value)}
          />
        );
      default:
        return (
          <input
            type="text"
            className="form-input"
            value={currentScalar}
            disabled={editingDisabled}
            onChange={(e) => onScalarChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div style={{
      padding: '0.875rem 1rem',
      borderBottom: '1px solid var(--border-color)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
        <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          {definition.displayName}
        </label>
        {isSystemOnly && <Badge tone="system-only">System Only</Badge>}
        {!isSystemOnly && isOverride && <Badge tone="override">Override</Badge>}
        {!isSystemOnly && isInherited && <Badge tone="inherited">Inherited</Badge>}
        {definition.unitLabel && (
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {definition.unitLabel}
          </span>
        )}
      </div>
      {definition.description && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          {definition.description}
        </div>
      )}
      <div style={{ maxWidth: '480px' }}>
        {renderControl()}
      </div>
      {scopeType !== 'SYSTEM' && allowsThisScope && (
        <div style={{ marginTop: '0.375rem', display: 'flex', gap: '0.625rem', alignItems: 'center' }}>
          {(isOverride || editedValue !== undefined || editedListValues !== undefined) && !isCleared && (
            <button
              type="button"
              onClick={onClearOverride}
              disabled={editingDisabled}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: '#dc2626', fontSize: '0.75rem', cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Reset to inherited
            </button>
          )}
          {isCleared && (
            <span style={{ fontSize: '0.75rem', color: '#d97706', fontStyle: 'italic' }}>
              Override will be cleared on save.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigSettingField;
